import { useEffect, useRef } from 'react';

/**
 * Re-runs `refetch` whenever the olSession transport changes.
 *
 * `socketId` is minted fresh by `createSession` on every reconnect, while
 * `sessionId` survives it. Query keys are therefore scoped to the stable
 * `sessionId` so reconnecting reuses one cache entry instead of minting — and
 * retaining — a new one per transport (the accumulation behind the 16 GB
 * incident). A reconnect still swaps the live socket underneath the same
 * session, so queries that talk over it must refresh against it. This hook
 * restores exactly the refetch that keying on `socketId` used to provide
 * implicitly, without the per-reconnect cache growth.
 *
 * `enabled` must be the query's own `enabled` condition. `refetch()` bypasses
 * React Query's `enabled` flag, so without this gate a reconnect while the
 * query is disabled (overlay with no viewport, period events with invalid
 * dates, a user without the events permission, …) would run its `queryFn` with
 * arguments that assume it is enabled.
 *
 * The transport at mount is treated as the baseline, so mounting a query does
 * not trigger a redundant refetch; only a subsequent change does. `refetch` is
 * stable across renders in React Query, so the effect does not loop.
 */
export const useRefetchOnSocketChange = (
  socketId: string | undefined,
  enabled: boolean,
  refetch: () => unknown,
): void => {
  const previousSocketIdRef = useRef(socketId);

  useEffect(() => {
    if (previousSocketIdRef.current === socketId) {
      return;
    }

    // Record the new transport even while the query is disabled: React Query
    // fetches by itself when `enabled` later flips true, so leaving the stale
    // baseline in place would turn that into a second, redundant refetch.
    previousSocketIdRef.current = socketId;

    if (!enabled) {
      return;
    }

    refetch();
  }, [socketId, enabled, refetch]);
};
